"use client";

import { useState } from "react";
import { FormLayout } from "../FormLayout";
import { ImageUpload } from "../ImageUpload";
import { ContactForm } from "./sections/ContactForm";
import { ClassifiedPreview } from "./sections/ClassifiedPreview";

export function HouseForm({
  step,
  maxSteps,
  onNext,
  onBack,
  onClose,
  formData,
  updateFormData,
  isProcessing,
}) {
  const [title, setTitle] = useState(formData.title || "");
  const [description, setDescription] = useState(formData.description || "");
  const [images, setImages] = useState(formData.images || []);
  const [propertyType, setPropertyType] = useState(formData.propertyType || "");
  const [bedrooms, setBedrooms] = useState(formData.bedrooms || "");
  const [bathrooms, setBathrooms] = useState(formData.bathrooms || "");
  const [price, setPrice] = useState(formData.price || "");
  const [listingType, setListingType] = useState(
    formData.listingType || "rent"
  );
  const [amenities, setAmenities] = useState(formData.amenities || []);
  const [story, setStory] = useState(formData.story || "");
  const [condition, setCondition] = useState(formData.condition || "");
  const [squareFoot, setSquareFoot] = useState(formData.squareFoot || "");
  const [isPreowned, setIsPreowned] = useState(formData.isPreowned || null);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateStep = (stepNumber) => {
    const newErrors = {};

    switch (stepNumber) {
      case 1: // Photos
        if (images.length < 6) {
          newErrors.images = "At least 6 photos are required";
        }
        break;
      case 2: // Title
        if (!title.trim()) {
          newErrors.title = "Title is required";
        } else if (title.trim().length < 2) {
          newErrors.title = "Title must be at least 2 characters long";
        }
        break;
      case 3: // Details
        if (!propertyType) {
          newErrors.propertyType = "Property type is required";
        }
        if (!bedrooms) {
          newErrors.bedrooms = "Number of bedrooms is required";
        }
        if (!bathrooms) {
          newErrors.bathrooms = "Number of bathrooms is required";
        }
        if (!condition) {
          newErrors.condition = "Condition rating is required";
        } else if (Number(condition) < 1 || Number(condition) > 10) {
          newErrors.condition = "Condition rating must be between 1 and 10";
        }
        if (!squareFoot) {
          newErrors.squareFoot = "Square footage is required";
        } else if (Number(squareFoot) <= 0) {
          newErrors.squareFoot = "Square footage must be greater than 0";
        }
        if (isPreowned === null) {
          newErrors.isPreowned =
            "Please select if the property is preowned or new";
        }
        break;
      case 4: // Price
        if (!price) {
          newErrors.price = "Price is required";
        } else if (price <= 0) {
          newErrors.price = "Price must be greater than 0";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Determine if Next button should be disabled
  const isNextDisabled = () => {
    switch (step) {
      case 1: // Photos
        return images.length < 6;
      case 2: // Title
        return !title.trim() || title.trim().length < 2;
      case 3: // Details
        return (
          !propertyType ||
          !bedrooms ||
          !bathrooms ||
          !condition ||
          Number(condition) < 1 ||
          Number(condition) > 10 ||
          !squareFoot ||
          Number(squareFoot) <= 0 ||
          isPreowned === null
        );
      case 4: // Price
        return !price || price <= 0;
      case 6: // Contact Form
        return (
          !formData.isContactFormValid ||
          !formData.fullName?.trim() ||
          !formData.email?.trim() ||
          !formData.phoneNumber?.trim() ||
          !formData.addressLine1?.trim() ||
          !formData.state?.trim()
        );
      default:
        return false;
    }
  };

  // Save data when moving between steps
  const handleNext = () => {
    if (!validateStep(step)) {
      return;
    }

    if (step === 3) {
      const specific_details = {
        propertyType,
        bedrooms,
        bathrooms,
        condition: Number(condition),
        squareFoot: Number(squareFoot),
        isPreowned,
        amenities,
      };
      updateFormData({
        ...formData,
        specific_details,
      });
    } else if (step === 6) {
      updateFormData({
        ...formData,
        contactName: formData.contactName || "",
        contactEmail: formData.contactEmail || "",
        contactPhone: formData.contactPhone || "",
        preferredContact: formData.preferredContact || "",
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        agreeTerms: formData.agreeTerms || false,
      });
    } else {
      updateFormData({
        title,
        description,
        images,
        propertyType,
        bedrooms,
        bathrooms,
        condition: Number(condition),
        squareFoot: Number(squareFoot),
        isPreowned,
        price: Number(price),
        listingType,
        amenities,
        story,
      });
    }
    onNext();
  };

  const handleAmenityChange = (amenity, checked) => {
    if (checked) {
      setAmenities([...amenities, amenity]);
    } else {
      setAmenities(amenities.filter((a) => a !== amenity));
    }
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
    if (newImages.length > 0 && errors.images) {
      setErrors((prev) => ({ ...prev, images: undefined }));
    }
  };

  const inputClassName =
    "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
  const errorInputClassName =
    "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

  const renderStepContent = () => {
    switch (step) {
      case 1: // Add Photos
        return (
          <div className="space-y-4">
            <ImageUpload
              category="House"
              minPhotos={6}
              images={images}
              onChange={handleImagesChange}
              isNextDisabled={isNextDisabled}
            />
            {errors.images && (
              <p className="text-red-500 text-sm mt-1">{errors.images}</p>
            )}
          </div>
        );

      case 2: // Add Title
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 font-fraunces"
              >
                Add title
              </label>
              <input
                id="title"
                type="text"
                placeholder="Enter a descriptive title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: undefined }));
                  }
                }}
                className={errors.title ? errorInputClassName : inputClassName}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
          </div>
        );

      case 3: // Key Details
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-medium font-fraunces">
              Add key details about your House
            </h2>

            <div className="space-y-3">
              <label
                htmlFor="propertyType"
                className="block text-sm font-medium text-gray-700"
              >
                Property Type *
              </label>
              <select
                id="propertyType"
                value={propertyType}
                onChange={(e) => {
                  setPropertyType(e.target.value);
                  if (errors.propertyType) {
                    setErrors((prev) => ({ ...prev, propertyType: undefined }));
                  }
                }}
                className={
                  errors.propertyType ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select property type</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
              </select>
              {errors.propertyType && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.propertyType}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="bedrooms"
                className="block text-sm font-medium text-gray-700"
              >
                Bedrooms *
              </label>
              <select
                id="bedrooms"
                value={bedrooms}
                onChange={(e) => {
                  setBedrooms(e.target.value);
                  if (errors.bedrooms) {
                    setErrors((prev) => ({ ...prev, bedrooms: undefined }));
                  }
                }}
                className={
                  errors.bedrooms ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select number of bedrooms</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5+">5+</option>
              </select>
              {errors.bedrooms && (
                <p className="text-red-500 text-sm mt-1">{errors.bedrooms}</p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="bathrooms"
                className="block text-sm font-medium text-gray-700"
              >
                Bathrooms *
              </label>
              <select
                id="bathrooms"
                value={bathrooms}
                onChange={(e) => {
                  setBathrooms(e.target.value);
                  if (errors.bathrooms) {
                    setErrors((prev) => ({ ...prev, bathrooms: undefined }));
                  }
                }}
                className={
                  errors.bathrooms ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select number of bathrooms</option>
                <option value="1">1</option>
                <option value="1.5">1.5</option>
                <option value="2">2</option>
                <option value="2.5">2.5</option>
                <option value="3+">3+</option>
              </select>
              {errors.bathrooms && (
                <p className="text-red-500 text-sm mt-1">{errors.bathrooms}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Condition Rating (1-10) *
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={condition}
                onChange={(e) => {
                  setCondition(e.target.value);
                  if (errors.condition) {
                    setErrors((prev) => ({ ...prev, condition: undefined }));
                  }
                }}
                placeholder="Enter rating from 1-10"
                className={
                  errors.condition ? errorInputClassName : inputClassName
                }
              />
              {errors.condition && (
                <p className="text-red-500 text-sm mt-1">{errors.condition}</p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="squareFoot"
                className="block text-sm font-medium text-gray-700"
              >
                Square Footage (sq ft) *
              </label>
              <input
                id="squareFoot"
                type="number"
                min="1"
                placeholder="Enter square footage"
                value={squareFoot}
                onChange={(e) => {
                  setSquareFoot(e.target.value);
                  if (errors.squareFoot) {
                    setErrors((prev) => ({ ...prev, squareFoot: undefined }));
                  }
                }}
                className={
                  errors.squareFoot ? errorInputClassName : inputClassName
                }
              />
              {errors.squareFoot && (
                <p className="text-red-500 text-sm mt-1">{errors.squareFoot}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Is the property preowned or new? *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="preowned-house"
                    name="isPreownedHouse"
                    checked={isPreowned === true}
                    onChange={() => {
                      setIsPreowned(true);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="preowned-house"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Preowned
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="new-house"
                    name="isPreownedHouse"
                    checked={isPreowned === false}
                    onChange={() => {
                      setIsPreowned(false);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="new-house"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    New
                  </label>
                </div>
              </div>
              {errors.isPreowned && (
                <p className="text-red-500 text-sm mt-1">{errors.isPreowned}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Highlight Your Amenities
              </label>
              <p className="text-sm text-gray-500 mb-4">
                that make your property stand out to attract the perfect
                tenants.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "parking", label: "Parking", icon: "🚗" },
                  { id: "wifi", label: "Wi-Fi", icon: "📶" },
                  {
                    id: "air-conditioning",
                    label: "Air Conditioning",
                    icon: "❄️",
                  },
                  { id: "heating", label: "Heating", icon: "🔥" },
                  { id: "pet-friendly", label: "Pet-Friendly", icon: "🐕" },
                  {
                    id: "laundry-in-unit",
                    label: "Laundry In-Unit",
                    icon: "👕",
                  },
                  {
                    id: "washer-dryer-on-site",
                    label: "Washer/Dryer On-Site",
                    icon: "🔄",
                  },
                  { id: "balcony-patio", label: "Balcony/Patio", icon: "🏠" },
                  { id: "fireplace", label: "Fireplace", icon: "🔥" },
                  {
                    id: "fully-equipped-kitchen",
                    label: "Fully Equipped Kitchen",
                    icon: "🍳",
                  },
                  { id: "gym", label: "Gym", icon: "💪" },
                  {
                    id: "gated-community",
                    label: "Gated Community",
                    icon: "🏘️",
                  },
                  {
                    id: "security-system",
                    label: "Security System",
                    icon: "🔒",
                  },
                ].map((amenity) => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() =>
                      handleAmenityChange(
                        amenity.id,
                        !amenities.includes(amenity.id)
                      )
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                      amenities.includes(amenity.id)
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <span>{amenity.icon}</span>
                    <span className="text-sm font-medium">{amenity.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4: // Price
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Select your perfect price
            </h2>
            <h4 className="text-sm font-medium font-afacad text-gray-500">
              Condition, features, and more - help others see the value!
            </h4>
            <div className="space-y-2">
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Price *
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                placeholder="$ Enter the amount"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) {
                    setErrors((prev) => ({ ...prev, price: undefined }));
                  }
                }}
                className={errors.price ? errorInputClassName : inputClassName}
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price}</p>
              )}
            </div>
          </div>
        );

      case 5: // Tell story
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Tell the story behind your House
            </h2>
            <p className="text-sm text-gray-500">
              Why sell? Share the details!
            </p>
            <div className="space-y-2">
              <label
                htmlFor="story"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for selling / Additional details
              </label>
              <textarea
                id="story"
                rows={6}
                placeholder="Tell your story..."
                value={story}
                onChange={(e) => setStory(e.target.value)}
                className={inputClassName}
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                More Description
              </label>
              <textarea
                id="description"
                rows={6}
                placeholder="Add a personal touch - what should buyers know?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClassName}
              />
            </div>
          </div>
        );

      case 6: // Contact details
        return (
          <ContactForm formData={formData} updateFormData={updateFormData} />
        );
      case 7: // Preview
        return <ClassifiedPreview formData={formData} category="House" />;
      default:
        return null;
    }
  };

  // Get step-specific title and description
  const getStepInfo = () => {
    const title = "Create Classified";
    let description = "";

    switch (step) {
      case 1:
        description = "Add photos of your House";
        break;
      case 2:
        description = "Add title and description";
        break;
      case 3:
        description = "Add key details";
        break;
      case 4:
        description = "Set price and options";
        break;
      case 5:
        description = "Tell your story";
        break;
      case 6:
        description = "Add your contact details";
        break;
      case 7:
        description = "Preview your classified";
        break;
    }

    return { title, description };
  };

  const { title: stepTitle, description: stepDescription } = getStepInfo();

  return (
    <FormLayout
      title={stepTitle}
      description={stepDescription}
      step={step}
      maxSteps={maxSteps}
      onNext={handleNext}
      onBack={onBack}
      onClose={onClose}
      isLastStep={step === maxSteps}
      isNextDisabled={isNextDisabled()}
      isProcessing={isProcessing}
    >
      {renderStepContent()}
    </FormLayout>
  );
}
